import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { validatePassword } from '@/generated/client/passwordreset/passwordreset';
import type { PasswordValidationRequest, PasswordValidationResponse } from '@/generated/model';

const PASSWORD_VALIDATION_DEBOUNCE_MS = 500;

type PasswordValidationInput = PasswordValidationRequest & {
  language: string;
};

type PasswordValidationMutationVariables = {
  input: PasswordValidationInput;
  signal: AbortSignal;
};

export type PasswordValidationStatus = 'idle' | 'pending' | 'success' | 'error';

function isSameInput(first: PasswordValidationInput | undefined, second: PasswordValidationInput | undefined) {
  return (
    first !== undefined &&
    second !== undefined &&
    first.username === second.username &&
    first.newPassword === second.newPassword &&
    first.sudoPassword === second.sudoPassword &&
    first.language === second.language
  );
}

function waitForValidationDelay(delay: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort);
      resolve();
    }, delay);

    function handleAbort() {
      window.clearTimeout(timeout);
      signal.removeEventListener('abort', handleAbort);
      reject(new Error('Password validation request aborted'));
    }

    if (signal.aborted) {
      handleAbort();
    } else {
      signal.addEventListener('abort', handleAbort, { once: true });
    }
  });
}

async function validatePasswordAfterDelay(input: PasswordValidationInput, signal: AbortSignal) {
  await waitForValidationDelay(PASSWORD_VALIDATION_DEBOUNCE_MS, signal);

  const response = await validatePassword(
    {
      username: input.username,
      newPassword: input.newPassword,
      sudoPassword: input.sudoPassword,
    },
    { signal }
  );
  return response.data;
}

export function usePasswordValidation({
  username,
  newPassword,
  sudoPassword,
  language,
  onError,
}: PasswordValidationInput & { onError?: (error: unknown) => void }) {
  const [requestedInput, setRequestedInput] = useState<PasswordValidationInput>();
  const [lastValidation, setLastValidation] = useState<PasswordValidationResponse>();
  const wasEmpty = useRef(true);

  const input = newPassword && sudoPassword ? { username, newPassword, sudoPassword, language } : undefined;

  const mutation = useMutation<PasswordValidationResponse, unknown, PasswordValidationMutationVariables>({
    mutationFn: ({ input: validationInput, signal }) => validatePasswordAfterDelay(validationInput, signal),
    retry: false,
    onMutate: (variables) => {
      setRequestedInput(variables.input);
      if (wasEmpty.current) {
        setLastValidation(undefined);
      }
      wasEmpty.current = false;
    },
    onSuccess: (validation, variables) => {
      if (isSameInput(input, variables.input)) {
        setLastValidation(validation);
      }
    },
    onError: (error, variables) => {
      if (!variables.signal.aborted && isSameInput(input, variables.input)) {
        onError?.(error);
      }
    },
  });

  const { data, error, isError, isPending, isSuccess, mutate, reset } = mutation;

  useEffect(() => {
    const requestInput = newPassword && sudoPassword ? { username, newPassword, sudoPassword, language } : undefined;

    if (!requestInput) {
      wasEmpty.current = true;
      reset();
      return;
    }

    const controller = new AbortController();
    mutate({ input: requestInput, signal: controller.signal });

    return () => controller.abort();
  }, [language, mutate, newPassword, reset, sudoPassword, username]);

  const hasCurrentInput = input !== undefined;
  const hasCurrentRequest = isSameInput(requestedInput, input);
  const status: PasswordValidationStatus = !hasCurrentInput
    ? 'idle'
    : !hasCurrentRequest || isPending
      ? 'pending'
      : isSuccess
        ? 'success'
        : isError
          ? 'error'
          : 'pending';

  return {
    error,
    isAccepted: status === 'success' && Boolean((data ?? lastValidation)?.accepted),
    isPending: status === 'pending',
    status,
    validation: hasCurrentInput ? (data ?? lastValidation) : undefined,
  };
}
